// ── 큰 카드 — 표/리스트/지도 위주 (2026-06-07) ───────────────────────────
// SbContent 분리. L 사이즈 카드 (홍보물/정산/접속/마케터별계약/상담자별계약/지도/최근계약).
//
// 2026-06-07 L 위젯 통일 디자인:
//   - 헤더 핵심값 (text-xl font-black accent text)
//   - 헤더 아래 구분선 (border-t border-{accent}-100 mx-1 my-1.5)
//   - 본문 (표/list/지도)
//   - watermark 우측 하단 100px opacity-10 accent (지도 X)

import React from 'react';
import { Package, Wallet, History, PackageMinus, Coins, Activity } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { normalizeIp, fmtTime, nameColor } from '../utils';
import { UnifiedCard } from '../UnifiedCard';
import { accentBorder } from '../widgetAccent';
import { ContractByMarketerPanel } from '../ContractByMarketerPanel';
import { ContractByConsultantPanel } from '../ContractByConsultantPanel';
import { RecentContractsCard } from '../RecentContractsCard';
import { TodayMeetingMapPanel } from '../TodayMeetingMapPanel';

// L 위젯 통일 헤더→본문 구분선 (border-t-{accent}-100, accent override 반영)
function HeaderDivider({ tone }) {
  const cls = accentBorder(tone) || '';
  // accentBorder 는 "border-{accent}-100 dark:border-{accent}-900/40" 형식 — 둘 다 사용
  return <div className={cn('border-t mx-1 my-1.5', cls)} />;
}

export function renderTableCard(code, ctx) {
  const { stats, accentMap, navOnClickFor, summarySet } = ctx;
  const ov = accentMap[code];
  // 위젯 메뉴가 없는 손님 테넌트 직원 — 계약 표는 카드 요약 길에서 그달 표를 받는다(2026-10-24, useWidgetSummaries)
  const summaryCode = summarySet && summarySet.has(code) ? code : undefined;
  // 카드 클릭 → 연결 메뉴 navigate (사용자 명세 2026-06-18). 매핑 없으면 undefined.
  const onCard = navOnClickFor ? navOnClickFor(code) : undefined;

  switch (code) {
    case 'promotional_profit':
      if (!stats.promoProfit || stats.promoProfit.data?.length === 0) {
        return (
          <UnifiedCard key={code} code={code} title="이번달 홍보물 수익" icon={Package} accent="pink" accentOverride={ov} size="L" sortableId={code} watermarkIcon={PackageMinus} onClick={onCard}>
            <p className="text-xs text-[var(--text-muted)] py-2">홍보물 수익 데이터 없음</p>
          </UnifiedCard>
        );
      }
      return (
        <UnifiedCard key={code} code={code} title="이번달 홍보물 수익" icon={Package} accent="pink" accentOverride={ov} size="L" sortableId={code} watermarkIcon={PackageMinus} onClick={onCard}>
          {/* 헤더 카운트: 합계 순수익 강조 */}
          <div className="flex items-baseline justify-end gap-1 mb-2">
            <span className={cn('text-xl font-black tabular-nums leading-none', (stats.promoProfit.totalProfit || 0) >= 0 ? 'text-pink-700 dark:text-pink-300' : 'text-red-500')}>
              {(stats.promoProfit.totalProfit || 0).toLocaleString()}
            </span>
            <span className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">순수익</span>
          </div>
          <HeaderDivider tone={ov || 'pink'} />
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['신청자', '매출', '원가', '순수익'].map((h, i) => (
                    <th key={h} className={cn('px-1.5 py-1 text-[10px] font-bold text-[var(--text-sub)] border-b-2 border-[var(--border)] bg-slate-50 dark:bg-pine-800/40', i === 0 ? 'text-left' : 'text-right')}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(stats.promoProfit.data || []).map((row) => (
                  <tr key={row.name} className="hover:bg-slate-50 dark:hover:bg-pine-800/30">
                    <td className="px-1.5 py-1 text-[11px] font-bold text-[var(--text)] border-b border-[var(--border)]">{row.name}</td>
                    <td className="px-1.5 py-1 text-[11px] tabular-nums text-[var(--text)] border-b border-[var(--border)] text-right">{row.revenue.toLocaleString()}</td>
                    <td className="px-1.5 py-1 text-[11px] tabular-nums text-[var(--text-sub)] border-b border-[var(--border)] text-right">{row.cost.toLocaleString()}</td>
                    <td className={cn('px-1.5 py-1 text-[11px] tabular-nums font-bold border-b border-[var(--border)] text-right', row.profit >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-500')}>{row.profit.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-pink-100/50 dark:bg-pink-900/30">
                  <td className="px-1.5 py-1 text-[11px] font-black text-pink-700 dark:text-pink-300 border-t border-[var(--border)]">합계</td>
                  <td className="px-1.5 py-1 text-[11px] tabular-nums font-bold text-[var(--text)] border-t border-[var(--border)] text-right">{(stats.promoProfit.totalRevenue || 0).toLocaleString()}</td>
                  <td className="px-1.5 py-1 text-[11px] tabular-nums text-[var(--text-sub)] border-t border-[var(--border)] text-right">{(stats.promoProfit.totalCost || 0).toLocaleString()}</td>
                  <td className={cn('px-1.5 py-1 text-[11px] tabular-nums font-black border-t border-[var(--border)] text-right', (stats.promoProfit.totalProfit || 0) >= 0 ? 'text-pink-700 dark:text-pink-300' : 'text-red-500')}>{(stats.promoProfit.totalProfit || 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </UnifiedCard>
      );

    case 'settlement_net': {
      const settlementTotal = (stats.settlementNet || []).reduce((s, it) => s + (Number(it.netTotal) || 0), 0);
      if (!stats.settlementNet || stats.settlementNet.length === 0) {
        return (
          <UnifiedCard key={code} code={code} title="정산예정금액" icon={Wallet} accent="emerald" accentOverride={ov} size="L" sortableId={code} watermarkIcon={Coins} onClick={onCard}>
            <p className="text-xs text-[var(--text-muted)] py-2">정산 데이터 없음</p>
          </UnifiedCard>
        );
      }
      return (
        <UnifiedCard key={code} code={code} title="정산예정금액" icon={Wallet} accent="emerald" accentOverride={ov} size="L" sortableId={code} watermarkIcon={Coins} onClick={onCard}>
          {/* 헤더 카운트: 합계 강조 */}
          <div className="flex items-baseline justify-end gap-1 mb-2">
            <span className={cn('text-xl font-black tabular-nums leading-none', settlementTotal >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-500')}>
              {settlementTotal >= 0 ? '+' : ''}{settlementTotal.toLocaleString()}
            </span>
            <span className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">합계</span>
          </div>
          <HeaderDivider tone={ov || 'emerald'} />
          {/* 2열 grid + 작은 폰트 — 모든 데이터 한눈에 (잘림 X) */}
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
            {stats.settlementNet.map((item) => (
              <div key={item.id} className="flex items-baseline justify-between gap-1 py-0.5 border-b border-[var(--border)] last:border-0 min-w-0">
                <span className="text-[10px] font-bold text-[var(--text)] truncate flex-shrink min-w-0">{item.name}</span>
                <span className={cn('text-[10px] font-black tabular-nums whitespace-nowrap flex-shrink-0', item.netTotal >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-500')}>
                  {item.netTotal >= 0 ? '+' : ''}{item.netTotal.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </UnifiedCard>
      );
    }

    case 'recent_online':
      if (stats.recentOnline.length === 0) {
        return (
          <UnifiedCard key={code} code={code} title="최근 접속" icon={History} accent="slate" accentOverride={ov} size="L" sortableId={code} watermarkIcon={Activity} onClick={onCard}>
            <p className="text-xs text-[var(--text-muted)] py-2">최근 접속 데이터가 없습니다.</p>
          </UnifiedCard>
        );
      }
      // 한 layout (모바일 친화적) — isMobile 분기 제거
      return (
        <UnifiedCard key={code} code={code} title="최근 접속" icon={History} accent="slate" accentOverride={ov} size="L" sortableId={code} watermarkIcon={Activity} onClick={onCard}>
          {/* 헤더 카운트 */}
          <div className="flex items-baseline justify-end gap-1 mb-2">
            <span className="text-xl font-black tabular-nums leading-none text-blue-700 dark:text-blue-300">{stats.recentOnline.length}</span>
            <span className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">명 접속</span>
          </div>
          <HeaderDivider tone={ov || 'slate'} />
          <div className="flex flex-col gap-2">
            {stats.recentOnline.map((row) => (
              <div key={`${row.user_id}-${row.last_seen_at}`} className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-black text-white"
                  style={{ background: `linear-gradient(135deg,${nameColor(row.name || '?')}cc,${nameColor(row.name || '?')})` }}
                >
                  {String(row.name || '?').slice(0, 1)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-[var(--text)]">{row.name || '-'}</div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{normalizeIp(row.ip_address) || '-'}</div>
                </div>
                <div className="text-[10px] text-[var(--text-muted)] flex-shrink-0">{fmtTime(row.last_seen_at)}</div>
              </div>
            ))}
          </div>
        </UnifiedCard>
      );

    case 'contracts_by_marketer':
      return <ContractByMarketerPanel key={code} accentOverride={ov} sortableId={code} onClick={onCard} summaryCode={summaryCode} />;

    case 'contracts_by_consultant':
      return <ContractByConsultantPanel key={code} accentOverride={ov} sortableId={code} onClick={onCard} summaryCode={summaryCode} />;

    case 'today_meeting_map':
      return (
        <TodayMeetingMapPanel
          key={code}
          meetings={stats.todayMeetingGroups}
          tomorrowMeetings={stats.tomorrowMeetingGroups}
          tmCalls={stats.tmCalls}
          points={stats.meetingPoints}
          today={stats.today}
          tomorrow={stats.tomorrow}
          accentOverride={ov}
          sortableId={code}
          onClick={onCard}
        />
      );

    case 'recent_contracts_mobile': {
      if (!stats.mobileTopContracts || stats.mobileTopContracts.length === 0) return null;
      return <RecentContractsCard key={code} code={code} groups={stats.mobileTopContracts} accentOverride={ov} sortableId={code} onClick={onCard} />;
    }

    default:
      return null;
  }
}
